<script lang="ts" setup generic="T">
import { TirPmIconView } from "tir-pm-icon-view";
import { PropType, computed, useSlots, defineEmits, ref, watch } from "vue";
import {
  CrossCompactIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
} from "tir-style-system/icons/outline";

import { NegativeCross, ChevronDownCompact } from "tir-style-system/icons/solid";
import { TirPmInput } from "tir-pm-input";
import {
  TirPmOptionList,
  TirPmOptionListGroupSeparator,
  TirPmOptionListItemElement,
} from "tir-pm-option-list";
import { TirPmSpinner } from "tir-pm-spinner";

const emit = defineEmits<{
  (eventName: "update:modelValue", value: string | number): void;
  (eventName: "input", value: string | number): void;
  (eventName: "onClearSearchInput"): void;
  (eventName: "enter"): void;
}>();

/**
 * Пропсы
 */
const props = defineProps({
  /**
   * Закрываемый
   */
  isSmall: { type: Boolean as PropType<boolean>, default: false },
  /**
   * Значение
   */
  modelValue: {
    type: [String, Number] as PropType<string | number>,
    default: null,
  },
  /**
   * Надпись когда ничего не найдено
   */
  isWithoutOptionLabel: { type: String as PropType<string>, default: "Ничего не нашлось" },
  /**
   * Использовать ли option-list
   */
  isWithPopup: { type: Boolean as PropType<boolean>, default: true },
  /**
   * Плейсхолдер для инпута
   */
  placeholder: { type: String as PropType<string>, default: "Плейсхолдер" },
  /**
   * Максимальная высота optionlist
   */
  maxHeight: { type: Number as PropType<number>, default: 240 },
  /**
   * Лейбл
   */
  label: { type: String as PropType<string>, default: "Лейбл" },
  /**
   * Идет ли загрузка
   */
  isLoading: { type: Boolean as PropType<boolean>, default: false },
  /**
   * Отключенный
   */
  isDisabled: { type: Boolean as PropType<boolean>, default: false },
});

/**
 * Слоты
 */
const slots = useSlots();

/**
 * Значение поиска
 */
const searchString = ref<string | number>("");

/**
 * Фокус у инпута
 */
const isFocused = ref(false);

/**
 * Значение
 */
const value = computed({
  get: () => {
    return props.modelValue == null ? searchString.value : props.modelValue;
  },
  set: (value) => {
    emit("update:modelValue", value);
    searchString.value = value;
  },
});

/**
 * Переключение фокуса
 */
const onChangeFocus = (focusValue: boolean) => {
  isFocused.value = focusValue;
};

/**
 * Очистить инпут поиска
 */
const onClearInput = () => {
  emit("onClearSearchInput");
  value.value = "";
};
/**
 * Событие ввода
 */
const onInput = (value: string | number) => {
  emit("input", value);
};

defineExpose({
  value: searchString,
});
</script>

<template>
  <div class="tir-pm-search-input">
    <div class="tir-pm-search-input__input-container">
      <TirPmInput
        :label="props.label"
        class="tir-pm-search-input__input"
        :placeholder="props.placeholder"
        v-model="value"
        @focus="onChangeFocus"
        @blur="onChangeFocus"
        @input="onInput"
        @enter="emit('enter')"
        :isAutocomplete="false"
        :isSmallSize="!props.isSmall"
        :isDisabled="props.isDisabled"
        :isMicro="props.isSmall"
        :isShowHint="false"
        :isWithHint="false"
      >
        <template #leftInputAddons>
          <MagnifyingGlassIcon class="tir-pm-search-input__glass-icon" />
        </template>
        <template #hintMessage>
          <slot name="hintMessage"></slot>
        </template>
        <template #rightInputAddons>
          <div class="tir-pm-search-input__right-click-area">
            <div
              class="tir-pm-search-input__chevron-button"
              @click.stop
              v-if="!searchString && !value && isWithPopup"
              :class="{
                'tir-pm-search-input__chevron-button--focused': isFocused,
              }"
            >
              <ChevronDownCompact class="tir-pm-search-input__chevron-icon" />
            </div>
            <div class="tir-pm-search-input__close-button" v-if="value" @click.stop="onClearInput">
              <NegativeCross class="tir-pm-search-input__close-icon" />
            </div>
          </div>
        </template>
      </TirPmInput>
    </div>
    <TirPmOptionList
      v-if="isWithPopup"
      :isOpened="isFocused"
      :maxHeight="props.maxHeight"
      :isUseClickOutside="false"
      :isWithoutOptionLabel="props.isWithoutOptionLabel"
    >
      <template #content>
        <slot name="content" v-if="!props.isLoading"> </slot>
        <TirPmOptionListItemElement v-if="props.isLoading" class="tir-pm-search-input__loading">
          <TirPmSpinner></TirPmSpinner>
        </TirPmOptionListItemElement>
      </template>
    </TirPmOptionList>
  </div>
</template>

<style lang="scss">
@import "./style.scss";
</style>
